/**
 * Scheduler Validation Utilities
 *
 * Zod schemas for validating service area and blackout date forms.
 * Ensures data integrity before submitting to database.
 */

import { z } from 'zod'

/**
 * Service Area validation schema
 *
 * Rules:
 * - Name must be non-empty
 * - Display order must be positive
 * - At least one day must be selected (otherwise area is unusable)
 * - Franchise region must be valid
 */
export const serviceAreaSchema = z
  .object({
    name: z.string().min(1, 'Service area name is required'),
    franchise_region: z.enum(['hamilton', 'northyork'], {
      errorMap: () => ({ message: 'Please select a valid franchise region' }),
    }),
    display_order: z.number().int().positive('Display order must be a positive number'),
    is_active: z.boolean(),
    monday: z.boolean(),
    tuesday: z.boolean(),
    wednesday: z.boolean(),
    thursday: z.boolean(),
    friday: z.boolean(),
    saturday: z.boolean(),
    sunday: z.boolean(),
  })
  .refine(
    (data) => {
      // At least one day must be selected
      return (
        data.monday ||
        data.tuesday ||
        data.wednesday ||
        data.thursday ||
        data.friday ||
        data.saturday ||
        data.sunday
      )
    },
    {
      message: 'At least one day must be selected for service availability',
      path: ['monday'], // Show error on first day field
    }
  )

export type ServiceAreaFormValues = z.infer<typeof serviceAreaSchema>

/**
 * Blackout Date validation schema
 *
 * Rules:
 * - Label must be non-empty
 * - Start and end dates are required
 * - End date must be >= start date
 * - Dates must be valid ISO date strings
 * - Franchise region is optional (null = all franchises)
 * - Service area ID is optional (null = all areas)
 */
export const blackoutDateSchema = z
  .object({
    label: z.string().min(1, 'Label is required'),
    start_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'Start date must be a valid date',
    }),
    end_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: 'End date must be a valid date',
    }),
    franchise_region: z.enum(['hamilton', 'northyork']).nullable().optional(),
    service_area_id: z.string().uuid().nullable().optional(),
    reason: z.string().nullable().optional(),
    is_active: z.boolean(),
  })
  .refine(
    (data) => {
      // Ensure end_date >= start_date
      const start = new Date(data.start_date)
      const end = new Date(data.end_date)
      return end >= start
    },
    {
      message: 'End date must be on or after start date',
      path: ['end_date'],
    }
  )

export type BlackoutDateFormValues = z.infer<typeof blackoutDateSchema>

/**
 * Note: Partial update schemas not exported because .partial() doesn't work
 * on Zod schemas with .refine(). For updates, validate full object or use
 * Supabase's partial update directly.
 */

/**
 * Helper function to validate service area data
 */
export function validateServiceArea(data: unknown): ServiceAreaFormValues {
  return serviceAreaSchema.parse(data)
}

/**
 * Helper function to validate blackout date data
 */
export function validateBlackoutDate(data: unknown): BlackoutDateFormValues {
  return blackoutDateSchema.parse(data)
}

/**
 * Helper function to safely validate service area (returns error instead of throwing)
 */
export function safeValidateServiceArea(data: unknown) {
  return serviceAreaSchema.safeParse(data)
}

/**
 * Helper function to safely validate blackout date (returns error instead of throwing)
 */
export function safeValidateBlackoutDate(data: unknown) {
  return blackoutDateSchema.safeParse(data)
}
